const STORAGE_KEY = 'linkedin_post_generator_settings';

const DEFAULT_PROMPTS = {
  generation: `You are a LinkedIn content strategist with 10+ years of experience creating viral, high-engagement posts. You understand LinkedIn's algorithm, audience psychology, and professional communication excellence.

🎯 YOUR MISSION: Write a professional LinkedIn post about "{title}"

📋 CORE REQUIREMENTS:
- Tone: {tone}
- Key Topics: {topics}
- Target Length: {length}
- Language: {language}

💎 PROFESSIONAL EXCELLENCE STANDARDS:
✅ Create scroll-stopping, value-packed content that solves real problems
✅ Use specific examples, data points, or personal stories (not generic advice)
✅ Write in an authentic, human voice that builds trust and connection
✅ Use short paragraphs (1-3 lines) and line breaks for easy scanning
✅ Add strategic emojis ({emojiUsage} usage) to enhance readability and emotion
✅ End with a powerful {ctaStyle} that drives meaningful comments

🚫 CRITICAL OUTPUT RULES:
- Do NOT add hashtags (they will be added automatically)
- Do NOT include any preamble like "Here's a post about..." or "Subject:"
- Do NOT add explanations or meta-commentary
- ONLY output the post content - ready to copy and paste directly to LinkedIn

📐 STRUCTURE REQUIRED:
1. 🎣 HOOK (First 1-2 lines): Create curiosity, surprise, or emotional resonance. Use a question, bold statement, or surprising fact.
2. 📖 VALUE (Middle section): Deliver on the promise. Include actionable insights, frameworks, or stories. Use bullet points or numbered lists for clarity.
3. 💬 CALL-TO-ACTION (Last 1-2 lines): Invite engagement with a thought-provoking question or clear action step.

🧠 ENGAGEMENT PSYCHOLOGY:
- Start with "You" or "Imagine" to create immediate connection
- Use power words: transform, discover, master, proven, secret, breakthrough
- Include pattern interrupts (line breaks, emojis, formatting)
- Create FOMO or curiosity gaps that make readers want to comment

✍️ Write the LinkedIn post now (ONLY the post content, nothing else):`,

  refinement: `You are a senior LinkedIn content editor specializing in transforming good posts into viral, engagement-driving content.

📝 ORIGINAL POST:
"""
{content}
"""

💬 USER'S IMPROVEMENT REQUEST:
"""
{feedback}
"""

🎯 YOUR TASK: Improve this post by applying the user's feedback PRECISELY while maintaining the core message.

✨ IMPROVEMENT FRAMEWORK:
1️⃣ CLARITY: Make every sentence crystal clear. Remove jargon unless necessary.
2️⃣ IMPACT: Strengthen weak words. Replace passive voice with active. Add specificity.
3️⃣ ENGAGEMENT: Enhance emotional resonance. Add strategic line breaks and emojis.
4️⃣ FLOW: Ensure smooth transitions. Check rhythm and pacing.
5️⃣ VALUE: Amplify actionable insights. Make it more useful.

🚫 CRITICAL RULES:
- Apply the user's feedback EXACTLY as requested
- Do NOT change the core message or main points
- Do NOT add hashtags
- Do NOT include any explanation or preamble
- ONLY output the improved post content - nothing else

✍️ Write the improved post now (ONLY the post, ready to copy-paste):`,

  hashtags: `You are a LinkedIn hashtag strategist with expertise in social media algorithms and content discovery optimization.

📊 POST DETAILS:
Title: {title}
Content: {content}

🎯 YOUR MISSION: Generate 5-8 strategic hashtags that maximize reach AND relevance.

💡 HASHTAG STRATEGY FRAMEWORK:
1️⃣ REACH MIX (30-40%): High-volume hashtags (1M+ posts) for broad discovery
2️⃣ NICHE TARGETING (30-40%): Medium-volume hashtags (100K-1M posts) for qualified audience
3️⃣ HYPER-SPECIFIC (20-30%): Low-volume hashtags (10K-100K posts) for engaged communities
4️⃣ LANGUAGE MIX: Include BOTH Arabic and English hashtags when appropriate

✅ QUALITY CRITERIA:
- Actually used on LinkedIn (not Twitter or Instagram tags)
- Mix of trending and evergreen tags
- Professional and business-appropriate
- Under 25 characters each

🚫 AVOID:
- Generic tags like #Monday #Motivation (unless specifically relevant)
- Duplicate or similar hashtags

📋 OUTPUT FORMAT: Provide ONLY the hashtags, one per line, starting with #, no explanations:`,

  imagePrompts: `You are an expert AI image prompt engineer specializing in professional LinkedIn business visuals. You create detailed, copy-paste ready prompts for Midjourney, Stable Diffusion, and DALL-E.

📝 LINKEDIN POST CONTENT:
"""
{content}
"""

🎯 YOUR MISSION: Analyze the post content carefully and create 3 professional image prompts that DIRECTLY VISUALIZE THE SPECIFIC TOPIC discussed in this post.

⚠️ CRITICAL REQUIREMENTS:
1. Focus on THE ACTUAL TOPIC of the post - NOT generic "LinkedIn" or "business" imagery
2. ALL prompts MUST be written in ENGLISH only (for AI image generators)
3. If any text/words appear in the image, they MUST be in ENGLISH - absolutely NO Arabic text or characters
4. Extract key concepts from the post and visualize THOSE SPECIFIC concepts
5. DO NOT use generic stock photo descriptions

🎨 IMAGE PROMPT REQUIREMENTS:
- Identify the MAIN SUBJECT from the post (e.g., "environmental sustainability", "AI technology", "cloud infrastructure", "team leadership", "data analytics")
- Create visuals that represent THAT SPECIFIC TOPIC, not just "professional business"
- Highly detailed (specific subject, style, colors, lighting, composition, mood)
- Professional and LinkedIn-appropriate but TOPIC-FOCUSED
- Diverse visual approaches (clean illustration, abstract concept art, infographic style)
- Include technical parameters for quality

✅ GOOD EXAMPLES (Topic-Focused):
❌ BAD: "Professional LinkedIn post visual, corporate aesthetic"
✅ GOOD: "Green energy sustainability concept with solar panels and wind turbines, eco-friendly color palette"

❌ BAD: "Modern business professional scene"
✅ GOOD: "Artificial intelligence neural network with glowing nodes and data flow, futuristic tech visualization"

❌ BAD: "Corporate office design"
✅ GOOD: "Cloud computing infrastructure with connected server networks, modern tech diagram style"

📋 PROMPT STRUCTURE:
[SPECIFIC TOPIC FROM POST] + [Visual Representation] + [Style/Art Direction] + [Colors] + [Technical Quality] + [Important: English text only if any]

📐 OUTPUT FORMAT: Provide exactly 3 prompts:

[PROMPT_1]
[Topic-focused prompt - analyze what the post discusses and create imagery about THAT SPECIFIC SUBJECT. Any text must be English only. Ready for Midjourney/DALL-E/Stable Diffusion]
[/PROMPT_1]

[PROMPT_2]
[Different visual style for same topic. Any text must be English only. Ready for Midjourney/DALL-E/Stable Diffusion]
[/PROMPT_2]

[PROMPT_3]
[Third unique approach to the post's topic. Any text must be English only. Ready for Midjourney/DALL-E/Stable Diffusion]
[/PROMPT_3]

🚫 ABSOLUTELY AVOID:
- Generic "LinkedIn professional" or "business meeting" descriptions
- Arabic text or characters of ANY kind
- People's faces or personal photos
- Copyrighted brand logos
- Stock photo clichés

REMEMBER: Read the post and create prompts about WHAT THE POST ACTUALLY DISCUSSES, not generic business imagery!`
};

const DEFAULT_PREFERENCES = {
  language: 'english',
  tone: 'professional',
  favoriteTopics: '',
  lengthType: 'medium',
  emojiUsage: 'moderate', // none, light, moderate, heavy
  hashtagCount: 6,
  ctaStyle: 'question' // question, action, discussion
};

class SettingsService {
  static getSettings() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    }

    return {
      prompts: { ...DEFAULT_PROMPTS },
      preferences: { ...DEFAULT_PREFERENCES }
    };
  }

  static saveSettings(settings) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
      return true;
    } catch (error) {
      console.error('Failed to save settings:', error);
      return false;
    }
  }

  static getPrompts() {
    const settings = this.getSettings();
    return settings.prompts || { ...DEFAULT_PROMPTS };
  }

  static savePrompts(prompts) {
    const settings = this.getSettings();
    settings.prompts = prompts;
    return this.saveSettings(settings);
  }

  static getPreferences() {
    const settings = this.getSettings();
    return settings.preferences || { ...DEFAULT_PREFERENCES };
  }

  static savePreferences(preferences) {
    const settings = this.getSettings();
    settings.preferences = preferences;
    return this.saveSettings(settings);
  }

  static resetToDefaults() {
    const settings = {
      prompts: { ...DEFAULT_PROMPTS },
      preferences: { ...DEFAULT_PREFERENCES }
    };
    return this.saveSettings(settings);
  }

  static exportSettings() {
    const settings = this.getSettings();
    const dataStr = JSON.stringify(settings, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);

    const link = document.createElement('a');
    link.href = url;
    link.download = 'linkedin-post-generator-settings.json';
    link.click();

    URL.revokeObjectURL(url);
  }

  static importSettings(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const settings = JSON.parse(e.target.result);
          if (this.saveSettings(settings)) {
            resolve(settings);
          } else {
            reject(new Error('Failed to save imported settings'));
          }
        } catch (error) {
          reject(new Error('Invalid settings file'));
        }
      };

      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  }

  static getDefaultPrompts() {
    return { ...DEFAULT_PROMPTS };
  }

  static getDefaultPreferences() {
    return { ...DEFAULT_PREFERENCES };
  }
}

export default SettingsService;
